/**
 * useAsyncProcessing Hook
 *
 * React hook for managing async document processing jobs with BullMQ.
 * Handles starting async jobs, polling for status, cancellation, and retry.
 * Falls back to sync processing if async queue is unavailable.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as mhrApi from '../lib/api';
import type { ProcessingJob, QueueProcessingResponse } from '../lib/api';
import type { MHRDocument } from '../types';

export type AsyncJobState =
  | 'idle'
  | 'starting'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'fallback_sync';

export interface AsyncProcessingState {
  jobId: string | null;
  documentId: string | null;
  state: AsyncJobState;
  progress: number;
  currentLayer: number;
  pagesProcessed: number;
  totalPages: number;
  layerProgress: {
    layer1: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
    layer2: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
    layer3: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
  };
  queuePosition: number | null;
  costs: {
    extraction: number;
    ocr: number;
    vision: number;
    total: number;
  };
  error: string | null;
  message: string;
  estimatedTimeRemaining: number | null;
}

export interface UseAsyncProcessingOptions {
  pollInterval?: number;
  onProgress?: (state: AsyncProcessingState) => void;
  // `final` carries the freshly-computed page counts at completion so callers do
  // not have to read a stale closure over the hook's `state` (which is 0/0 at the
  // time the callback was created). Omitted where counts are unknown.
  onComplete?: (documentId: string, final?: { pagesProcessed: number; totalPages: number }) => void;
  onError?: (error: string) => void;
}

export interface UseAsyncProcessingResult {
  state: AsyncProcessingState;
  startProcessing: (documentId: string, options?: { skipOcr?: boolean; skipVision?: boolean }) => Promise<void>;
  pollExistingJobs: (documentId: string) => Promise<void>;
  cancelJob: () => Promise<void>;
  retryJob: () => Promise<void>;
  isProcessing: boolean;
  canCancel: boolean;
  canRetry: boolean;
}

const DEFAULT_STATE: AsyncProcessingState = {
  jobId: null,
  documentId: null,
  state: 'idle',
  progress: 0,
  currentLayer: 0,
  pagesProcessed: 0,
  totalPages: 0,
  layerProgress: {
    layer1: { status: 'pending', progress: 0, pagesProcessed: 0 },
    layer2: { status: 'pending', progress: 0, pagesProcessed: 0 },
    layer3: { status: 'pending', progress: 0, pagesProcessed: 0 },
  },
  queuePosition: null,
  costs: {
    extraction: 0,
    ocr: 0,
    vision: 0,
    total: 0,
  },
  error: null,
  message: 'Ready to process',
  estimatedTimeRemaining: null,
};

export function useAsyncProcessing(
  options: UseAsyncProcessingOptions = {}
): UseAsyncProcessingResult {
  const { pollInterval = 2000, onProgress, onComplete, onError } = options;

  const [state, setState] = useState<AsyncProcessingState>(DEFAULT_STATE);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    if (!mountedRef.current) return;

    try {
      // Cast to any — BullMQ returns raw data (state: 'waiting'/'active',
      // progress as last updateProgress() value) that doesn't match ProcessingJob type
      const job = await mhrApi.getJobStatus(jobId) as any;

      if (!mountedRef.current) return;

      // Safely access progress (BullMQ returns raw progress object from updateProgress())
      const progress = job.progress || {};
      const defaultLayerProgress = {
        layer1: { status: 'pending' as const, progress: 0, pagesProcessed: 0 },
        layer2: { status: 'pending' as const, progress: 0, pagesProcessed: 0 },
        layer3: { status: 'pending' as const, progress: 0, pagesProcessed: 0 },
      };

      // Map job state to our state (BullMQ returns 'waiting', 'active', etc.)
      let newState: AsyncJobState;
      switch (job.state) {
        case 'pending':
        case 'waiting':
        case 'delayed':
          newState = 'queued';
          break;
        case 'layer1_extraction':
        case 'layer2_ocr':
        case 'layer3_vision':
          newState = 'processing';
          break;
        case 'active':
          newState = 'processing';
          break;
        case 'completed':
          newState = 'completed';
          break;
        case 'failed':
          newState = 'failed';
          break;
        default:
          newState = 'processing';
      }

      // Calculate current layer from job state or progress.stage
      let currentLayer = 0;
      if (job.state === 'layer1_extraction' || progress.stage === 'layer1') currentLayer = 1;
      else if (job.state === 'layer2_ocr' || progress.stage === 'layer2') currentLayer = 2;
      else if (job.state === 'layer3_vision' || progress.stage === 'layer3') currentLayer = 3;
      else if (progress.stage === 'summary' || progress.stage === 'aggregation') currentLayer = 3;

      // Build message
      let message = 'Processing...';
      const pagesProcessed = progress.pagesProcessed ?? 0;
      const totalPages = progress.totalPages ?? 0;
      if (newState === 'queued') {
        message = 'Waiting in queue...';
      } else if (newState === 'processing') {
        const layerNames = ['', 'Extracting text', 'Running OCR', 'Verifying with vision'];
        message = progress.message || `${layerNames[currentLayer] || 'Processing'}...${totalPages > 0 ? ` (${pagesProcessed}/${totalPages} pages)` : ''}`;
      } else if (newState === 'completed') {
        message = 'Processing complete!';
      } else if (newState === 'failed') {
        message = job.error || (job as any).failedReason || 'Processing failed';
      }

      const newStateData: AsyncProcessingState = {
        jobId,
        documentId: job.documentId || (job as any).data?.documentId,
        state: newState,
        progress: progress.overall ?? progress.progress ?? (newState === 'completed' ? 100 : 0),
        currentLayer,
        pagesProcessed,
        totalPages,
        layerProgress: progress.layerProgress || defaultLayerProgress,
        queuePosition: null,
        costs: job.costs || { extraction: 0, ocr: 0, vision: 0, total: 0 },
        error: job.error || (job as any).failedReason || null,
        message,
        estimatedTimeRemaining: null,
      };

      setState(newStateData);
      onProgress?.(newStateData);

      // Handle completion
      if (newState === 'completed') {
        stopPolling();
        onComplete?.(job.documentId, {
          pagesProcessed: newStateData.pagesProcessed,
          totalPages: newStateData.totalPages,
        });
      }

      // Handle failure
      if (newState === 'failed') {
        stopPolling();
        onError?.(job.error || 'Processing failed');
      }
    } catch (err: any) {
      console.error('[useAsyncProcessing] Poll error:', err);
      // Don't stop polling on transient errors, but limit retries
      if (err.response?.status === 404) {
        stopPolling();
        setState(prev => ({
          ...prev,
          state: 'failed',
          error: 'Job not found',
          message: 'Job not found - it may have been cancelled',
        }));
        onError?.('Job not found');
      }
    }
  }, [stopPolling, onProgress, onComplete, onError]);

  // Start polling
  const startPolling = useCallback((jobId: string) => {
    stopPolling(); // Clear any existing polling

    // Initial poll
    pollJobStatus(jobId);

    // Set up interval
    pollingRef.current = setInterval(() => {
      pollJobStatus(jobId);
    }, pollInterval);
  }, [pollInterval, pollJobStatus, stopPolling]);

  // Start async processing
  const startProcessing = useCallback(async (
    documentId: string,
    processingOptions?: { skipOcr?: boolean; skipVision?: boolean }
  ) => {
    setState(prev => ({
      ...DEFAULT_STATE,
      documentId,
      state: 'starting',
      message: 'Starting async processing...',
    }));

    try {
      const response = await mhrApi.startAsyncProcessing(documentId, processingOptions);

      if (!mountedRef.current) return;

      setState(prev => ({
        ...prev,
        jobId: response.jobId,
        state: 'queued',
        message: 'Job queued successfully',
      }));

      // Start polling for status
      startPolling(response.jobId);
    } catch (err: any) {
      console.error('[useAsyncProcessing] Start error:', err);

      // Check if it's a "queue unavailable" error - fall back to sync
      const errorMessage = err.response?.data?.error || err.message || 'Failed to start processing';
      const isQueueUnavailable =
        errorMessage.toLowerCase().includes('queue') ||
        errorMessage.toLowerCase().includes('redis') ||
        errorMessage.toLowerCase().includes('unavailable') ||
        err.response?.status === 503;

      if (isQueueUnavailable) {
        // Fall back to synchronous processing
        setState(prev => ({
          ...prev,
          state: 'fallback_sync',
          message: 'Queue unavailable, using synchronous processing...',
        }));

        try {
          // Use sync processing as fallback
          await mhrApi.startProcessing(documentId);

          if (!mountedRef.current) return;

          setState(prev => ({
            ...prev,
            state: 'completed',
            progress: 100,
            message: 'Processing complete (sync fallback)',
          }));

          onComplete?.(documentId);
        } catch (syncErr: any) {
          console.error('[useAsyncProcessing] Sync fallback error:', syncErr);
          const syncError = syncErr.response?.data?.error || 'Sync processing failed';
          setState(prev => ({
            ...prev,
            state: 'failed',
            error: syncError,
            message: syncError,
          }));
          onError?.(syncError);
        }
      } else {
        setState(prev => ({
          ...prev,
          state: 'failed',
          error: errorMessage,
          message: errorMessage,
        }));
        onError?.(errorMessage);
      }
    }
  }, [startPolling, onComplete, onError]);

  // Cancel the current job
  const cancelJob = useCallback(async () => {
    if (!state.jobId) return;

    try {
      const result = await mhrApi.cancelJob(state.jobId);
      stopPolling();

      if (result.success) {
        setState(prev => ({
          ...prev,
          state: 'cancelled',
          message: 'Job cancelled',
        }));
      } else {
        setState(prev => ({
          ...prev,
          error: result.message || 'Could not cancel job',
          message: result.message || 'Could not cancel job',
        }));
      }
    } catch (err: any) {
      console.error('[useAsyncProcessing] Cancel error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to cancel job';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        message: errorMessage,
      }));
    }
  }, [state.jobId, stopPolling]);

  // Retry a failed job
  const retryJob = useCallback(async () => {
    if (!state.jobId) return;

    setState(prev => ({
      ...prev,
      state: 'starting',
      error: null,
      message: 'Retrying job...',
    }));

    try {
      const response = await mhrApi.resumeJob(state.jobId);

      if (!mountedRef.current) return;

      setState(prev => ({
        ...prev,
        jobId: response.jobId,
        state: 'queued',
        message: 'Job requeued successfully',
      }));

      startPolling(response.jobId);
    } catch (err: any) {
      console.error('[useAsyncProcessing] Retry error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to retry job';
      setState(prev => ({
        ...prev,
        state: 'failed',
        error: errorMessage,
        message: errorMessage,
      }));
      onError?.(errorMessage);
    }
  }, [state.jobId, startPolling, onError]);

  // Poll for existing jobs for a document (used when showing status for already-started processing)
  const pollExistingJobs = useCallback(async (documentId: string) => {
    setState(prev => ({
      ...DEFAULT_STATE,
      documentId,
      state: 'starting',
      message: 'Looking for existing jobs...',
    }));

    try {
      const { jobs } = await mhrApi.getDocumentJobs(documentId);

      if (!mountedRef.current) return;

      // Find the most recent active or pending job
      const activeJob = jobs.find(
        j => j.state !== 'completed' && j.state !== 'failed'
      );

      if (activeJob) {
        setState(prev => ({
          ...prev,
          jobId: activeJob.id,
          state: 'queued',
          message: 'Found existing job, tracking progress...',
        }));

        // Start polling for this job
        startPolling(activeJob.id);
      } else {
        // No active job found - document may be completed or failed
        const completedJob = jobs.find(j => j.state === 'completed');
        const failedJob = jobs.find(j => j.state === 'failed');

        if (completedJob) {
          setState(prev => ({
            ...prev,
            jobId: completedJob.id,
            state: 'completed',
            progress: 100,
            message: 'Processing complete!',
          }));
          onComplete?.(documentId);
        } else if (failedJob) {
          setState(prev => ({
            ...prev,
            jobId: failedJob.id,
            state: 'failed',
            error: failedJob.error || 'Processing failed',
            message: failedJob.error || 'Processing failed',
          }));
          onError?.(failedJob.error || 'Processing failed');
        } else {
          // No jobs found at all - check document status via document API
          setState(prev => ({
            ...prev,
            state: 'idle',
            message: 'No processing jobs found',
          }));
        }
      }
    } catch (err: any) {
      console.error('[useAsyncProcessing] Poll existing jobs error:', err);

      // If 404, no jobs exist - check if document status indicates processing
      if (err.response?.status === 404) {
        // Fall back to checking document status
        try {
          const status = await mhrApi.getDocumentStatus(documentId);
          if (status.status === 'completed') {
            setState(prev => ({
              ...prev,
              state: 'completed',
              progress: 100,
              message: 'Processing complete!',
              pagesProcessed: status.pages_processed,
              totalPages: status.page_count,
            }));
            onComplete?.(documentId, {
              pagesProcessed: status.pages_processed,
              totalPages: status.page_count,
            });
          } else if (status.status === 'failed') {
            setState(prev => ({
              ...prev,
              state: 'failed',
              error: status.error || 'Processing failed',
              message: status.error || 'Processing failed',
            }));
            onError?.(status.error || 'Processing failed');
          } else {
            // Still processing but no job found - poll document status instead
            setState(prev => ({
              ...prev,
              state: 'processing',
              message: `Processing... (${status.pages_processed}/${status.page_count} pages)`,
              pagesProcessed: status.pages_processed,
              totalPages: status.page_count,
            }));
          }
        } catch {
          setState(prev => ({
            ...prev,
            state: 'idle',
            message: 'No processing status available',
          }));
        }
      } else {
        const errorMessage = err.response?.data?.error || 'Failed to get job status';
        setState(prev => ({
          ...prev,
          state: 'failed',
          error: errorMessage,
          message: errorMessage,
        }));
        onError?.(errorMessage);
      }
    }
  }, [startPolling, onComplete, onError]);

  // Computed properties
  const isProcessing = ['starting', 'queued', 'processing', 'fallback_sync'].includes(state.state);
  const canCancel = ['queued', 'processing'].includes(state.state);
  const canRetry = state.state === 'failed' && state.jobId !== null;

  return {
    state,
    startProcessing,
    pollExistingJobs,
    cancelJob,
    retryJob,
    isProcessing,
    canCancel,
    canRetry,
  };
}

export default useAsyncProcessing;
